/**
 * Vuex mutations for the "group" (org) module.
 *
 * Synchronous state writers for org data/lists, membership and moderation
 * lists, followed channels, and the group's feed posts (including reply
 * integration bookkeeping). Async work (API calls) lives in operations.js,
 * which commits these mutations with the results.
 */
export default {
  setOrgTypes(state, data) {
    state.orgTypes = data;
  },
  setOrgData(state, data) {
    state.orgData = data;
  },
  setMyOrgList(state, data) {
    state.myOrgList = data;
  },
  /** Prepend `org` to `myOrgList` if not already present (used for optimistic add-after-create). */
  addMyOrg(state, org) {
    if (!state.myOrgList) state.myOrgList = [];
    const exists = state.myOrgList.some(o => o.id === org.id);
    if (!exists) {
      state.myOrgList.unshift(org);
    }
  },
  setOtherOrgList(state, data) {
    state.otherOrgList = data;
  },
  setSearchMembers(state, data) {
    state.serchMembers = data;
  },
  setNoSearchMembers(state, query) {
    state.noSearchMembers = query;
  },
  setCurrentProfileOrg(state, data) {
    state.currentOrgProfile = data;
  },
  setNoSearchOrgs(state, query) {
    state.noSearchOrgs = query;
  },
  setNumFollowersChannel(state, data) {
    state.numFollowersChannel = data;
  },
  setOrgPosts(state, posts) {
    state.orgPosts = posts;
  },
  appendOrgPosts(state, posts) {
    state.orgPosts = [...state.orgPosts, ...posts];
  },
  /** Replace (or append to, for `orgPosts`) an arbitrary named state collection with `data`. */
  async setCollectionOrg(state, { collectionName, data, isReply }) {
    if (Array.isArray(state[collectionName])) {
      if (collectionName === 'orgPosts') {
        state[collectionName] = [...state[collectionName], ...data];
      } else {
        state[collectionName] = [...data];
      }
    }
  },
  /**
   * Add a post to `orgPosts`, or, if it's a reply to a post already in the
   * list, fold it into that parent's `integratedReplies` instead of adding
   * it as a standalone entry.
   */
  addOrgPost(state, post) {
    if (!state.orgPosts) {
      state.orgPosts = [];
    }
    
    // If this is a reply, try to integrate it with the parent post
    if (post.type === 'reply' && post.repostedPostId) {
      const parentPost = state.orgPosts.find(p => p.id === post.repostedPostId);
      if (parentPost) {
        // Initialize integrated replies if not already present
        if (!parentPost.integratedReplies) {
          parentPost.integratedReplies = [];
          parentPost.hasIntegratedReplies = true;
          parentPost.totalRepliesCount = 0;
        }
        
        // Add the new reply to the parent's integrated replies
        parentPost.integratedReplies.unshift(post);
        parentPost.totalRepliesCount = parentPost.integratedReplies.length;
        
        // Don't add the reply as a standalone post
        return;
      }
    }
    
    // For non-replies or replies without a parent, add as standalone post
    state.orgPosts.unshift(post);
  },
  // The generic `deletePost` action (store/main/actions.js) always commits
  // "removePost" — a main-module mutation that filters state[activeTab.collection].
  // orgPosts lives in this separate module, so that commit can never reach it
  // (it silently no-ops with "Collection not found"). Deleting a standalone
  // group post/reply never updated org.vue's own list without this.
  removeOrgPost(state, postId) {
    if (!Array.isArray(state.orgPosts)) return;
    state.orgPosts = state.orgPosts.filter((p) => p?.id !== postId);
    // Defensive: also check nested integratedReplies in case this is called
    // for a reply that wasn't already handled by PostWithReplies.vue's own
    // local splice.
    state.orgPosts.forEach((p) => {
      if (Array.isArray(p?.integratedReplies)) {
        const idx = p.integratedReplies.findIndex((r) => r?.id === postId);
        if (idx !== -1) {
          p.integratedReplies.splice(idx, 1);
          if (typeof p.totalRepliesCount === 'number') {
            p.totalRepliesCount = Math.max(0, p.totalRepliesCount - 1);
          }
        }
      }
    });
  },
  // The generic `updatePost` action (store/home/operations.js) always commits
  // "postPost"/"setPost" — home-module mutations that only patch
  // state[activeTab.collection] (recommendedPosts/followingPosts/etc). orgPosts
  // lives in this separate module, so those commits can never reach it. Editing
  // a group post never updated org.vue's/channel.vue's own list without this.
  updateOrgPost(state, updatedPost) {
    if (!Array.isArray(state.orgPosts) || !updatedPost?.id) return;
    // Reassign the whole array (matches removeOrgPost's proven-working pattern below)
    // rather than mutating it in place with splice — a plain in-place splice on a
    // post buried inside a long, already-rendered list wasn't reliably reaching the
    // mounted <Post> instance's props until something else forced a re-render.
    state.orgPosts = state.orgPosts.map((p) => {
      if (!p) return p;
      if (p.id === updatedPost.id) return { ...p, ...updatedPost };
      if (Array.isArray(p.integratedReplies)) {
        const ridx = p.integratedReplies.findIndex((r) => r?.id === updatedPost.id);
        if (ridx !== -1) {
          const integratedReplies = p.integratedReplies.slice();
          integratedReplies.splice(ridx, 1, { ...integratedReplies[ridx], ...updatedPost });
          return { ...p, integratedReplies };
        }
      }
      if (p.repostedPost?.id === updatedPost.id) {
        return { ...p, repostedPost: { ...p.repostedPost, ...updatedPost } };
      }
      return p;
    });
  },
  /** Apply incremental `delta` changes (reposts/replies/reply count/repost users) to one post in `orgPosts`. */
  updateOrgPostCounts(state, { postId, delta }) {
    if (!Array.isArray(state.orgPosts)) return;
    const idx = state.orgPosts.findIndex(p => p && p.id === postId);
    if (idx !== -1) {
      const post = state.orgPosts[idx];
      const updated = { ...post };
      if (delta.numReposts !== undefined) updated.numReposts = (post.numReposts || 0) + delta.numReposts;
      if (delta.numReplies !== undefined) updated.numReplies = (post.numReplies || 0) + delta.numReplies;
      if (delta.totalRepliesCount !== undefined) updated.totalRepliesCount = (post.totalRepliesCount || 0) + delta.totalRepliesCount;
      if (delta.repostedByUsersIds !== undefined) updated.repostedByUsersIds = delta.repostedByUsersIds;
      state.orgPosts.splice(idx, 1, updated);
    }
  },
  /** Replace `reactionSets` on the matching post in `orgPosts`, including when it's nested as a `repostedPost`. */
  updateOrgPostReactions(state, { postId, reactionSets }) {
    if (!Array.isArray(state.orgPosts)) return;
    const safeReactionSets = Array.isArray(reactionSets) ? reactionSets : [];
    state.orgPosts.forEach((p, idx) => {
      if (!p) return;
      if (p.id === postId) {
        state.orgPosts.splice(idx, 1, { ...p, reactionSets: safeReactionSets });
      } else if (p.repostedPost?.id === postId) {
        state.orgPosts.splice(idx, 1, {
          ...p,
          repostedPost: { ...p.repostedPost, reactionSets: safeReactionSets },
        });
      }
    });
  },
  /** Set the integrated replies list and total count on the matching post in `orgPosts`. */
  updateOrgIntegratedReplies(state, { postId, replies, total }) {
    if (!Array.isArray(state.orgPosts)) return;
    const idx = state.orgPosts.findIndex(p => p && p.id === postId);
    if (idx !== -1) {
      const post = state.orgPosts[idx];
      state.orgPosts.splice(idx, 1, {
        ...post,
        hasIntegratedReplies: true,
        integratedReplies: replies,
        totalRepliesCount: typeof total === 'number' ? total : (replies?.length || 0),
      });
    }
  },
  setStatusLoad(state, status) {
    state.isLoading = status;
  },
  setJoinRequests(state, requests) {
    state.joinRequests = requests;
  },
  clearJoinRequests(state) {
    state.joinRequests = [];
  },
  setBannedUsers(state, data) {
    state.bannedUsersList = data;
  },
  addFollowingChannel(state, channel) {
    if (!state.followingChannels) {
      state.followingChannels = [];
    }
    state.followingChannels.push(channel);
  },
  removeFollowingChannel(state, channelId) {
    if (!state.followingChannels) return;
    state.followingChannels = state.followingChannels.filter(
      (c) => c.channelId !== channelId
    );
  },
  setFollowingChannels(state, channels) {
    state.followingChannels = channels;
  },
};
